/**
 * The pointer's road into the Carry machine.
 *
 * The core is pure: it turns pointer samples, a timer and modifier keys into
 * Carry inputs, and knows three things the machine does not: the long-press
 * clock on touch, a fling (a fast release over nothing sends a run to the
 * Void), and which modifier keys are down. The DOM binding below it adds
 * pointer capture, click-carry drops, and external drops of a mailed verify
 * link (`text/uri-list` or `text/plain`).
 */

import type { CarryState, Held, Modifier, Target, Unsub } from '../contracts';
import { LONG_PRESS, type HandInput } from './carry';
import type { Registry } from './targets';
import { isTyping } from '../ui/a11y';

/** Pixels per millisecond over the last samples above which a release over nothing is a fling. */
export const FLING = 1.1;
const FLING_WINDOW = 90;

export interface Timers { set(fn: () => void, ms: number): unknown; clear(id: unknown): void }

export interface PointerDeps {
  dispatch(i: HandInput): void;
  state(): CarryState;
  resolve(x: number, y: number): Target | null;
  /** Where a flung thing goes: the Void of its World, when it has one. */
  voidFor(held: Held): Target | null;
  timers: Timers;
  now(): number;
}

const MOD_KEYS: Readonly<Record<string, Modifier>> = { s: 'soft', e: 'explain', q: 'queue' };

export interface PointerCore {
  down(held: Held, x: number, y: number, pointer: 'mouse' | 'touch' | 'pen', button?: number): boolean;
  move(x: number, y: number): void;
  up(x: number, y: number): void;
  cancel(): void;
  /** A key went down or up; true when the carry used it. */
  key(key: string, down: boolean): boolean;
}

export const createPointerCore = (deps: PointerDeps): PointerCore => {
  let timer: unknown = null;
  let samples: { x: number; y: number; t: number }[] = [];
  const stop = (): void => {
    if (timer !== null) deps.timers.clear(timer);
    timer = null;
  };
  const speed = (): number => {
    const now = deps.now();
    const recent = samples.filter((p) => now - p.t <= FLING_WINDOW);
    if (recent.length < 2) return 0;
    const a = recent[0]!;
    const b = recent[recent.length - 1]!;
    const dt = Math.max(1, b.t - a.t);
    return Math.hypot(b.x - a.x, b.y - a.y) / dt;
  };
  return {
    down(held, x, y, pointer, button = 0) {
      if (button !== 0 || deps.state().phase !== 'idle') return false;
      stop();
      samples = [{ x, y, t: deps.now() }];
      deps.dispatch({ type: 'press', held, at: { x, y }, pointer });
      if (pointer === 'touch') {
        timer = deps.timers.set(() => {
          timer = null;
          deps.dispatch({ type: 'timer', elapsed: LONG_PRESS });
        }, LONG_PRESS);
      }
      return true;
    },
    move(x, y) {
      const phase = deps.state().phase;
      if (phase !== 'pressed' && phase !== 'dragging') return;
      samples.push({ x, y, t: deps.now() });
      if (samples.length > 8) samples = samples.slice(-8);
      deps.dispatch({ type: 'move', at: { x, y }, over: deps.resolve(x, y) });
      if (deps.state().phase !== 'pressed') stop();
    },
    up(x, y) {
      stop();
      const s = deps.state();
      if (s.phase === 'dragging' && s.held) {
        const over = deps.resolve(x, y);
        const flung = !over && speed() >= FLING ? deps.voidFor(s.held) : null;
        deps.dispatch({ type: 'move', at: { x, y }, over: over ?? flung });
      }
      if (s.phase === 'pressed' || s.phase === 'dragging') deps.dispatch({ type: 'release' });
      samples = [];
    },
    cancel() {
      stop();
      samples = [];
      const phase = deps.state().phase;
      if (phase !== 'idle' && phase !== 'ring') deps.dispatch({ type: 'cancel' });
    },
    key(key, down) {
      const phase = deps.state().phase;
      if (key === 'Escape' && down && (phase === 'pressed' || phase === 'dragging')) {
        stop();
        deps.dispatch({ type: 'cancel' });
        return true;
      }
      const mod = MOD_KEYS[key.toLowerCase()];
      if (mod && phase === 'dragging') {
        deps.dispatch({ type: 'mod', mod, on: down });
        return true;
      }
      return false;
    },
  };
};

// ---------- scrolling at the edges ----------

/** How deep the band along a scrolling box's edge is (px), and how fast a carry at the very edge scrolls it (px per ms). */
export const EDGE_BAND = 64;
export const EDGE_MAX = 1.3;

/**
 * How fast a carry at `p` scrolls a box spanning lo..hi on one axis (px per ms, negative toward lo):
 * nothing away from the edges, then faster the deeper into the edge's band, easing in, and flat out at
 * and past the edge. A box too small for two bands gets narrower ones.
 */
export const edgeSpeed = (p: number, lo: number, hi: number, band = EDGE_BAND, max = EDGE_MAX): number => {
  const b = Math.max(8, Math.min(band, (hi - lo) / 3));
  if (p < lo + b) return -max * Math.min(1, (lo + b - p) / b) ** 2;
  if (p > hi - b) return max * Math.min(1, (p - (hi - b)) / b) ** 2;
  return 0;
};

/** Whether a box can still scroll by `d` on its axis (it is not already at that end). */
export const canScroll = (pos: number, size: number, view: number, d: number): boolean =>
  d < 0 ? pos > 0.5 : d > 0 ? pos + view < size - 0.5 : false;

const scrolls = (el: Element, axis: 'x' | 'y'): boolean => {
  const style = getComputedStyle(el);
  const o = axis === 'y' ? style.overflowY : style.overflowX;
  const box = el as HTMLElement;
  return (o === 'auto' || o === 'scroll' || o === 'overlay') && (axis === 'y' ? box.scrollHeight > box.clientHeight + 1 : box.scrollWidth > box.clientWidth + 1);
};

/** The nearest box under `el` that scrolls on `axis`, else the page when it does. */
export const scrollerOf = (el: Element | null, axis: 'x' | 'y', doc: Document = document): HTMLElement | null => {
  for (let at = el; at && at !== doc.documentElement; at = at.parentElement) if (scrolls(at, axis)) return at as HTMLElement;
  const page = doc.scrollingElement as HTMLElement | null;
  return page && (axis === 'y' ? page.scrollHeight > page.clientHeight + 1 : page.scrollWidth > page.clientWidth + 1) ? page : null;
};

// ---------- the mailed link ----------

/**
 * The verify token in a mailed link, from a dropped URL or pasted text:
 * `…/#/verify?token=sbv_…`, or the bare `sbv_…`. Null when there is none.
 */
export const verifyToken = (text: string): string | null => {
  const inLink = /[#?&]token=(sbv_[A-Za-z0-9_-]+)/.exec(text);
  if (inLink) return inLink[1]!;
  const bare = /(?:^|[^A-Za-z0-9_])(sbv_[A-Za-z0-9_-]+)/.exec(text);
  return bare ? bare[1]! : null;
};

/** A dropped link as a thing to carry. Its label never holds the token. */
export const linkHeld = (token: string): Held => ({ kind: 'link', id: 'link:verify', label: 'the mailed link', data: { token } });

// ---------- the DOM binding ----------

export interface PointerBinding {
  bindSource(el: HTMLElement, held: () => Held | null): Unsub;
  unbind(): void;
}

const pointerKind = (t: string): 'mouse' | 'touch' | 'pen' => (t === 'touch' ? 'touch' : t === 'pen' ? 'pen' : 'mouse');

/** Controls used during a carry (the S/E/Q lips): a tap on one sets a modifier, and neither drops nor puts back. */
export const CARRY_CONTROL = 'data-carry-control';
const inControl = (el: EventTarget | null): boolean =>
  typeof (el as Element | null)?.closest === 'function' && (el as Element).closest(`[${CARRY_CONTROL}]`) !== null;

/**
 * Pointer events on `root`: a press on a source goes to the core with the
 * pointer captured; while something is carried by a click, the next click
 * drops it on the target under it (or picks up the source under it, or puts
 * it back). The click that follows a drag or a drop is swallowed so the
 * element under it does not act twice. A carry control (a lip) is let
 * through: its own click sets the modifier and the carry goes on.
 */
export const bindPointer = (opts: {
  root: HTMLElement; core: PointerCore; registry: Registry; state(): CarryState; dispatch(i: HandInput): void;
  /** The target under a point for what is carried now (defaults to the innermost). */
  resolve?(x: number, y: number): Target | null;
}): PointerBinding => {
  const { root, core, registry } = opts;
  const resolve = (x: number, y: number): Target | null => (opts.resolve ? opts.resolve(x, y) : registry.resolve(x, y));
  let phaseAtDown: CarryState['phase'] = 'idle';
  let swallow = false;
  const offs: Unsub[] = [];
  const on = <K extends keyof HTMLElementEventMap>(el: HTMLElement | Window, type: K, fn: (e: HTMLElementEventMap[K]) => void, capture = false): void => {
    el.addEventListener(type, fn as EventListener, capture);
    offs.push(() => el.removeEventListener(type, fn as EventListener, capture));
  };

  on(root, 'pointerdown', () => {
    phaseAtDown = opts.state().phase;
    // A new press: the click a drag or drop might have been followed by never came (a touch drag sends none).
    swallow = false;
  }, true);

  // A carry near the edge of a box that scrolls (a long gallery, the Deck, a phone sheet) scrolls it, so a target
  // below the fold is one drag away; what is under the hand is judged again as the content moves beneath it.
  let last: { x: number; y: number } | null = null;
  let raf = 0;
  let lastT = 0;
  const carryingNow = (): boolean => { const p = opts.state().phase; return p === 'dragging' || p === 'carrying'; };
  const edgeTick = (t: number): void => {
    raf = 0;
    if (!last || !carryingNow()) { lastT = 0; return; }
    const dt = lastT ? Math.min(40, t - lastT) : 16;
    lastT = t;
    const doc = root.ownerDocument;
    // What scrolls under the hand, looking through the chrome that floats over a screen (the Director, the Wire).
    const stack = typeof doc.elementsFromPoint === 'function' ? doc.elementsFromPoint(last.x, last.y) : [doc.elementFromPoint(last.x, last.y)];
    let moved = false;
    for (const axis of ['y', 'x'] as const) {
      let box: HTMLElement | null = null;
      for (const el of stack) { box = scrollerOf(el, axis, doc); if (box && box !== doc.scrollingElement) break; }
      if (!box) continue;
      const page = box === doc.scrollingElement;
      const cs = page ? null : getComputedStyle(box);
      const pad = (v: string | undefined): number => (v ? parseFloat(v) || 0 : 0) * 0.6;
      // The edges are where the screen's content shows: inside the room it keeps clear of the floating chrome.
      const r0 = page ? { top: 0, bottom: doc.defaultView?.innerHeight ?? 0, left: 0, right: doc.defaultView?.innerWidth ?? 0 } : box.getBoundingClientRect();
      const r = { top: r0.top + pad(cs?.paddingTop), bottom: r0.bottom - pad(cs?.paddingBottom), left: r0.left + pad(cs?.paddingLeft), right: r0.right - pad(cs?.paddingRight) };
      const v = axis === 'y' ? edgeSpeed(last.y, r.top, r.bottom) : edgeSpeed(last.x, r.left, r.right);
      const d = v * dt;
      if (!d) continue;
      const ok = axis === 'y' ? canScroll(box.scrollTop, box.scrollHeight, box.clientHeight, d) : canScroll(box.scrollLeft, box.scrollWidth, box.clientWidth, d);
      if (!ok) continue;
      if (axis === 'y') box.scrollTop += d; else box.scrollLeft += d;
      moved = true;
    }
    if (moved) {
      if (opts.state().phase === 'dragging') core.move(last.x, last.y);
      else opts.dispatch({ type: 'move', at: last, over: resolve(last.x, last.y) });
    }
    raf = requestAnimationFrame(edgeTick);
  };
  offs.push(() => { if (raf) cancelAnimationFrame(raf); raf = 0; });

  on(root, 'pointermove', (e) => {
    const phase = opts.state().phase;
    if (phase === 'carrying' && !inControl(e.target)) opts.dispatch({ type: 'move', at: { x: e.clientX, y: e.clientY }, over: resolve(e.clientX, e.clientY) });
    if (phase === 'dragging' || phase === 'carrying') {
      last = { x: e.clientX, y: e.clientY };
      if (!raf && typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(edgeTick);
    } else last = null;
  });

  on(root, 'pointerup', (e) => {
    const phase = opts.state().phase;
    if (phaseAtDown !== 'carrying' || phase !== 'carrying') return;
    phaseAtDown = 'idle';
    if (inControl(e.target)) return;
    const target = resolve(e.clientX, e.clientY);
    const source = registry.sourceOf(e.target as Element | null);
    const held = source?.held() ?? null;
    swallow = true;
    if (target) opts.dispatch({ type: 'click', thing: { target } });
    else if (held) opts.dispatch({ type: 'click', thing: { held } });
    else opts.dispatch({ type: 'cancel' });
  });

  // Right-click opens the verb ring, as Enter does (§3.1).
  on(root, 'contextmenu', (e) => {
    if (opts.state().phase !== 'idle' || isTyping(e.target)) return;
    const el = e.target as Element | null;
    const subject = registry.sourceOf(el)?.held() ?? registry.targetOf(el)?.target() ?? null;
    if (!subject) return;
    e.preventDefault();
    opts.dispatch({ type: 'key-ring', subject });
  });

  on(root, 'click', (e) => {
    if (!swallow) return;
    swallow = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  const keys = (down: boolean) => (e: KeyboardEvent): void => {
    if (isTyping(e.target) && e.key !== 'Escape') return;
    if (core.key(e.key, down)) e.preventDefault();
  };
  on(window, 'keydown', keys(true));
  on(window, 'keyup', keys(false));
  on(window, 'blur', () => core.cancel());

  // A mailed link dropped from another window: carry it, and drop it where it landed.
  on(root, 'dragover', (e) => {
    const types = e.dataTransfer?.types ?? [];
    if (!isTyping(e.target) && (types.includes('text/uri-list') || types.includes('text/plain'))) e.preventDefault();
  });
  on(root, 'drop', (e) => {
    if (isTyping(e.target) || !e.dataTransfer) return;
    const text = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
    const token = verifyToken(text);
    if (!token) return;
    e.preventDefault();
    if (opts.state().phase !== 'idle') opts.dispatch({ type: 'cancel' });
    opts.dispatch({ type: 'click', thing: { held: linkHeld(token) } });
    const target = resolve(e.clientX, e.clientY);
    if (target) opts.dispatch({ type: 'click', thing: { target } });
  });

  return {
    bindSource(el, held) {
      const down = (e: PointerEvent): void => {
        if (isTyping(e.target)) return;
        const h = held();
        if (!h) return;
        if (!core.down(h, e.clientX, e.clientY, pointerKind(e.pointerType), e.button)) return;
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          // A synthetic or already-released pointer: the drag still works through the root listeners.
        }
        if (e.pointerType === 'mouse') {
          e.preventDefault(); // no text selection while dragging
          el.focus({ preventScroll: true });
        }
      };
      const move = (e: PointerEvent): void => core.move(e.clientX, e.clientY);
      const up = (e: PointerEvent): void => {
        const phase = opts.state().phase;
        if (phase !== 'pressed' && phase !== 'dragging') return;
        core.up(e.clientX, e.clientY);
        swallow = true;
      };
      const cancel = (): void => {
        const phase = opts.state().phase;
        if (phase === 'pressed' || phase === 'dragging') core.cancel();
      };
      // In a strip a finger scrolls sideways (touch-action: pan-x), a swipe scrolls; a long press lifts,
      // and from then on the finger is the carry's: the first move after the lift starts no scroll.
      const touchmove = (e: TouchEvent): void => {
        if (opts.state().phase === 'dragging' && e.cancelable) e.preventDefault();
      };
      el.addEventListener('pointerdown', down);
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', cancel);
      el.addEventListener('lostpointercapture', cancel);
      el.addEventListener('touchmove', touchmove, { passive: false });
      return () => {
        el.removeEventListener('pointerdown', down);
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', cancel);
        el.removeEventListener('lostpointercapture', cancel);
        el.removeEventListener('touchmove', touchmove);
      };
    },
    unbind() {
      for (const off of offs.splice(0)) off();
    },
  };
};
